import KeyboardKeyIcon from '@/components/ui/keyboard-key-icon';
import PaddockAvatar from '@/components/ui/paddock-avatar/paddock-avatar';
import { PDKNoUserIcon } from '@/components/ui/pdk-icons';
import getCallMeBy from '@/components/user/get-call-me-by';
import HotKeyListener from '@/components/ux/hot-key-listener';
import useUsers from '@/hooks/use-users';
import { HOTKEYS } from '@/lib/hotkeys';
import { pdkAxios } from '@/lib/pdk-axios';
import { dealStore } from '@/models/stores';
import type { Deal } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import compact from 'lodash/compact';
import { useMemo, useRef } from 'react';
import { ComboboxPopover } from '../../ui/combobox/combobox-popover';
import FinanceManagerBadge from './finance-manager-badge';

export const NO_FINANCE_MANAGER_OPTION = {
  value: 'no-finance-manager',
  label: 'No Finance Manager',
  icon: <PDKNoUserIcon className="w-5 h-5 rounded-full mr-2" />,
};

export const transformToFinanceManagerOptions = (
  financeManagers: NonNullable<Deal['finance_manager']>[],
) => {
  return [
    NO_FINANCE_MANAGER_OPTION,
    ...financeManagers.map((manager) => {
      const manager_full_name =
        `${manager.first_name} ${manager.last_name}`.trim();

      return {
        value: manager.id,
        label: getCallMeBy(manager),
        icon: (
          <PaddockAvatar
            src={manager.avatar}
            alt={getCallMeBy(manager)}
            fallback={`${manager.first_name.charAt(0)} ${manager.last_name.charAt(0)}`}
            className="w-5 h-5 rounded-full mr-2"
          />
        ),
        searchTerms: compact([getCallMeBy(manager), manager_full_name]),
      };
    }),
  ];
};

export type FinanceManagerSelectorProps = {
  id: Deal['id'];
  value: Deal['finance_manager'];
  hideName?: boolean;
  side?: 'left' | 'right' | 'bottom' | 'top';
  align?: 'center' | 'start' | 'end';
  withHotkeyListener?: boolean;
  useAbbreviations?: boolean;
  className?: string;
};

export function FinanceManagerSelector({
  id,
  value,
  hideName: hideNameAndCopy = false,
  side = 'left',
  align = 'start',
  withHotkeyListener = false,
  useAbbreviations = false,
  className,
}: FinanceManagerSelectorProps) {
  const { financeManagers } = useUsers();

  const updateFinanceManagerMutation = useMutation({
    onMutate: async ({ financeManager }) => {
      dealStore.updateFinanceManager({
        id,
        financeManager,
      });
    },
    mutationFn: async ({
      financeManager,
    }: {
      financeManager: Deal['finance_manager'];
    }) => {
      if (!financeManager) return;

      const response = await pdkAxios
        .post(`/deals/${id}/finance-manager/${financeManager.id}`, {
          finance_manager_id: financeManager.id.toString(),
        })
        .catch((e) => {
          console.error('Error updating finance manager', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating finance manager', error, variables);
    },
    onSettled: () => {
      // DEV NOTE: invalidateQueries is cause a flicker bug
      // because the useQuery hook in useDeal re runs and gives back an old value
      // It may not even be refetching ...
      // queryClient.invalidateQueries({ queryKey: ["deal", id] });
    },
  });

  const deleteFinanceManagerMutation = useMutation({
    onMutate: async () => {
      dealStore.updateFinanceManager({
        id,
        financeManager: null,
      });
    },
    mutationFn: async ({
      currentFinanceManager,
    }: {
      currentFinanceManager: Deal['finance_manager'];
    }) => {
      const response = await pdkAxios
        .post(
          `/deals/${id}/finance-manager/${currentFinanceManager?.id}/delete`,
        )
        .catch((e) => {
          console.error('Error deleting finance manager', e);
          throw e;
        });

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error deleting finance manager', error, variables);
    },
    onSettled: () => {
      // DEV NOTE: invalidateQueries is cause a flicker bug
      // because the useQuery hook in useDeal re runs and gives back an old value
      // queryClient.invalidateQueries({ queryKey: ["deal", id] });
    },
  });

  const financeManagerOptions = useMemo(() => {
    return transformToFinanceManagerOptions(financeManagers);
  }, [financeManagers]);

  const handleFinanceManagerChange = (newFinanceManagerId: string) => {
    if (newFinanceManagerId === 'no-finance-manager') {
      deleteFinanceManagerMutation.mutate({
        currentFinanceManager: value,
      });
      return;
    }

    const newFinanceManager = financeManagers.find(
      (option) => option.id === newFinanceManagerId,
    );

    if (!newFinanceManager) return;

    updateFinanceManagerMutation.mutate({
      financeManager: newFinanceManager,
    });
  };

  const financeManager = useMemo(() => {
    return financeManagers.find((option) => option.id === value?.id);
  }, [financeManagers, value]);

  const financeManagerRef = useRef<HTMLButtonElement>(null);

  const currentOption = useMemo(() => {
    return financeManagerOptions.find(
      (option) => option.value === (value?.id ?? 'no-finance-manager'),
    );
  }, [financeManagerOptions, value]);

  return (
    <ComboboxPopover
      endAdornment={
        <KeyboardKeyIcon>{HOTKEYS.DEAL_FINANCE_MANAGER}</KeyboardKeyIcon>
      }
      placeholder={'Set finance manager...'}
      onChange={handleFinanceManagerChange}
      emptyMessage="No finance managers found"
      currentOption={currentOption}
      options={financeManagerOptions}
      side={side}
      align={align}
    >
      <div className={className}>
        <FinanceManagerBadge
          financeManager={financeManager}
          hideNameAndCopy={hideNameAndCopy}
          useAbbreviations={useAbbreviations}
          financeManagerRef={financeManagerRef}
        />
        {withHotkeyListener && (
          <HotKeyListener
            hotkeys={HOTKEYS.DEAL_FINANCE_MANAGER}
            onHotkeyPressed={() => financeManagerRef.current?.click()}
          />
        )}
      </div>
    </ComboboxPopover>
  );
}
